
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { Building, HardHat, Layers, ChartBar, Users, FileText, Wrench, Construction } from "lucide-react";

type FunctionalArea = {
  id: string;
  name: string;
  description: string;
};

const functionalAreas: FunctionalArea[] = [
  {
    id: "project-delivery",
    name: "Project Delivery Methods",
    description: "Design-bid-build, design-build, IPD, CM at risk, and other delivery approaches"
  },
  {
    id: "construction-tech",
    name: "Construction Technology",
    description: "Digital tools, BIM implementation, field technology, and automation systems"
  },
  {
    id: "workforce-safety",
    name: "Workforce & Safety",
    description: "Labor optimization, safety protocols, training, and skills development"
  },
  {
    id: "cost-management",
    name: "Cost Management",
    description: "Budget optimization, cost control systems, estimating, and value engineering"
  },
  {
    id: "lean-methods",
    name: "Lean Construction",
    description: "Last Planner System, pull planning, waste reduction, and continuous improvement"
  },
  {
    id: "prefab-modular",
    name: "Prefabrication & Modular",
    description: "Off-site construction, modular solutions, and assembly optimization"
  },
  {
    id: "sustainability",
    name: "Sustainability & ESG",
    description: "Green building practices, environmental compliance, and social responsibility"
  },
  {
    id: "supply-chain",
    name: "Supply Chain & Procurement",
    description: "Material management, supplier relations, and procurement strategies"
  }
];

const DemoCallForSolutionsFunctionalArea = () => {
  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  const handleContinue = () => {
    if (selectedArea) {
      const selectedAreaName = functionalAreas.find(area => area.id === selectedArea)?.name || selectedArea;
      navigate("/demo-call-for-solutions/bounty", {
        state: {
          selectedArea: selectedAreaName
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <Card className="w-full max-w-3xl border border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold text-center mb-6">Call for Solution - Select Functional Area</h2>
          
          <RadioGroup 
            value={selectedArea || ""} 
            onValueChange={setSelectedArea}
            className="space-y-4"
          >
            {functionalAreas.map(area => (
              <div 
                key={area.id} 
                className="flex items-start space-x-2 p-2 rounded-md hover:bg-gray-50"
              >
                <RadioGroupItem value={area.id} id={area.id} className="mt-1" />
                <div className="flex-1">
                  <Label 
                    htmlFor={area.id} 
                    className="font-medium cursor-pointer flex items-center"
                  >
                    {area.name} <span className="text-gray-500 ml-2">–</span>
                  </Label>
                  <p className="text-gray-600 text-sm ml-2">{area.description}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
          
          <div className="flex justify-between mt-8">
            <Button 
              variant="outline" 
              onClick={() => navigate("/demo-call-for-solutions")}
              className="border-gray-300"
            >
              Back
            </Button>
            <Button 
              onClick={handleContinue}
              disabled={!selectedArea}
              className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoCallForSolutionsFunctionalArea;
